import React, { useState } from 'react';
import { Plus, Search, Filter, MoreHorizontal, X, ChevronRight, FileText } from 'lucide-react';
import { FieldDefinition, GenericResource } from '../types';

interface ResourceListProps {
  title: string;
  data: GenericResource[];
  columns: {
    header: string;
    accessor: string;
    render?: (value: any, item: any) => React.ReactNode;
  }[];
  fields?: FieldDefinition[]; // If provided, enables "Add" functionality
  onAdd?: (newItem: any) => void;
  emptyIcon?: React.ElementType;
  description?: string;
}

const ResourceList: React.FC<ResourceListProps> = ({
  title,
  data,
  columns,
  fields,
  onAdd,
  emptyIcon: EmptyIcon = FileText,
  description,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});

  // Filter data based on search term (naive search across all string fields)
  const filteredData = data.filter((item) =>
    Object.values(item).some((val) => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onAdd) {
      onAdd({ ...formData, id: Math.random().toString(36).substr(2, 9) });
    }
    setFormData({});
    setIsModalOpen(false);
  };

  const handleInputChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50">
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-200 bg-white flex justify-between items-center shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-800 tracking-tight">{title}</h2>
          {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all w-64"
            />
          </div>
          <button className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-md text-sm font-medium hover:bg-slate-50 flex items-center gap-2 transition-colors">
            <Filter size={16} />
            Filter
          </button>
          {fields && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-3 py-1.5 bg-cfleet-600 text-white rounded-md text-sm font-medium hover:bg-cfleet-700 shadow-sm flex items-center gap-2 transition-colors"
            >
              <Plus size={16} />
              Add {title.slice(0, -1)} {/* Crude singularization */}
            </button>
          )}
        </div>
      </div>

      {/* List Content */}
      <div className="flex-1 overflow-auto p-6">
        {filteredData.length > 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {columns.map((col, idx) => (
                    <th key={idx} className="px-6 py-3 font-medium">
                      {col.header}
                    </th>
                  ))}
                  <th className="px-6 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredData.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-slate-50/80 transition-colors group cursor-pointer"
                  >
                    {columns.map((col, idx) => (
                      <td key={idx} className="px-6 py-3 text-sm text-slate-700 whitespace-nowrap">
                        {col.render
                          ? col.render(item[col.accessor], item)
                          : String(item[col.accessor] ?? '')}
                      </td>
                    ))}
                    <td className="px-6 py-3 text-right">
                      <button className="text-slate-400 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        <ChevronRight size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-slate-400">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <EmptyIcon size={32} />
            </div>
            <h3 className="text-lg font-medium text-slate-900">No {title.toLowerCase()} found</h3>
            <p className="text-sm mt-1">Get started by adding a new record.</p>
          </div>
        )}
      </div>

      {/* Dynamic Add Modal */}
      {isModalOpen && fields && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-lg text-slate-800">Add New {title.slice(0, -1)}</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {field.label}
                  </label>
                  {field.type === 'select' ? (
                    <div className="relative">
                      <select
                        required
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 appearance-none"
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                      >
                        <option value="">Select an option</option>
                        {field.options?.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      <ChevronRight
                        className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 text-slate-400 pointer-events-none"
                        size={14}
                      />
                    </div>
                  ) : (
                    <input
                      type={field.type}
                      required
                      placeholder={field.placeholder}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 placeholder:text-slate-400"
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                    />
                  )}
                </div>
              ))}

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-cfleet-600 text-white rounded-lg text-sm font-medium hover:bg-cfleet-700 shadow-sm transition-colors"
                >
                  Create Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceList;
